// Tournament Match Scheduler (113) -- builder + public bracket view.
//
// Managers (super/org/host school_admin) create a draft, choose size + seeding,
// seed teams per-team, and publish. Everyone (incl. anonymous) sees the
// read-only bracket once published. One page covers both states.

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  TOURNAMENT_BRACKET_SIZES,
  type TournamentResponse,
  type TournamentSeedTeam,
  type TournamentRoundPublic,
  type TournamentMatchPublic,
  type TournamentSlot,
} from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EventWhereWho } from "@/components/event-detail/event-where-who";
import { EventInfoPanel } from "@/components/event-detail/info-panel";
import { ChevronLeft, Trophy, Plus, X, Shuffle, ListOrdered, Loader2 } from "lucide-react";

export default function TournamentPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TournamentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .getTournament(id)
      .then((res) => setData(res))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load tournament"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => reload(), [reload]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4" data-page="tournament" data-state="loading">
        <div className="h-6 w-40 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <p className="text-sm text-destructive" data-page="tournament" data-state="error" data-error>
        {error ?? "Tournament not found"}
      </p>
    );
  }

  const manage = data._actions.can_manage;
  const published = data.bracket != null && data.bracket.status !== "draft";

  return (
    <div className="flex flex-col gap-6" data-page="tournament" data-state="ready">
      <div className="flex flex-col gap-1">
        <Link to={`/events/${id}`} className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-3.5 w-3.5" /> Back to event
        </Link>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Trophy className="h-6 w-6 text-primary" aria-hidden />
          {data.event.name || "Tournament bracket"}
        </h1>
        {/* 194 -- hosting venue (with directions) + participating schools, so
            the public bracket page shows where + who, not just the bracket. */}
        <EventWhereWho event={data.event} className="mt-1" />
      </div>

      {/* 196 -- public event info pack (gate times, accommodation, travel).
          Reads visible blocks anonymously; renders nothing if there are none. */}
      <EventInfoPanel eventId={id!} venues={data.event.venues} publicView />

      {!data.bracket && (manage ? <CreateBracket eventId={id!} onCreated={reload} /> : <NoBracket />)}

      {data.bracket && !published && manage && (
        <SeedBuilder eventId={id!} data={data} onChange={reload} />
      )}

      {data.bracket && published && <BracketView rounds={data.rounds} />}

      {data.bracket && !published && !manage && <NoBracket pending />}
    </div>
  );
}

function NoBracket({ pending }: { pending?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-6 py-10 text-center">
      <Trophy className="mx-auto mb-3 h-6 w-6 text-muted-foreground" aria-hidden />
      <p className="text-sm text-muted-foreground">
        {pending ? "The bracket is being set up and hasn't been published yet." : "No bracket has been set up for this event yet."}
      </p>
    </div>
  );
}

// --- Create (draft) -------------------------------------------------------

function CreateBracket({ eventId, onCreated }: { eventId: string; onCreated: () => void }) {
  const [size, setSize] = useState<number>(8);
  const [rule, setRule] = useState<"manual" | "random">("manual");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setErr(null);
    try {
      await api.createTournament(eventId, { bracket_size: size, seeding_rule: rule });
      onCreated();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to create bracket");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-md flex-col gap-4 rounded-lg border border-border p-5">
      <p className="text-sm text-muted-foreground">
        Build a single-elimination bracket. The system generates the matches and a first-pass schedule across the event's venues.
      </p>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Bracket size</span>
        <select
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          data-field="bracket-size"
        >
          {TOURNAMENT_BRACKET_SIZES.map((s) => (
            <option key={s} value={s}>{s} teams</option>
          ))}
        </select>
      </label>
      <fieldset className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Seeding</span>
        <div className="flex gap-2">
          {(["manual", "random"] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRule(r)}
              data-field={`seeding-${r}`}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition ${
                rule === r ? "border-primary bg-primary/5 text-foreground" : "border-input text-muted-foreground hover:text-foreground"
              }`}
            >
              {r === "manual" ? <ListOrdered className="h-3.5 w-3.5" /> : <Shuffle className="h-3.5 w-3.5" />}
              {r === "manual" ? "Manual" : "Random"}
            </button>
          ))}
        </div>
      </fieldset>
      {err && <p className="text-sm text-destructive" data-error>{err}</p>}
      <Button onClick={create} disabled={busy} data-action="create-bracket" className="w-fit">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
        Create bracket
      </Button>
    </div>
  );
}

// --- Seed + publish (draft, manager) --------------------------------------

function SeedBuilder({
  eventId,
  data,
  onChange,
}: {
  eventId: string;
  data: TournamentResponse;
  onChange: () => void;
}) {
  const bracket = data.bracket!;
  const pool = data.entered_teams;
  const random = bracket.seeding_rule === "random";

  // Manual seeding: ordered list of team_ids (seed 1 = index 0).
  const [seeded, setSeeded] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const byId = new Map(pool.map((t) => [t.team_id, t]));
  const available = pool.filter((t) => !seeded.includes(t.team_id));
  const size = bracket.bracket_size;
  const minTeams = Math.floor(size / 2) + 1; // tightest-size rule from the API
  const enough = random ? pool.length >= minTeams : seeded.length >= minTeams && seeded.length <= size;
  const byes = random ? Math.max(0, size - Math.min(pool.length, size)) : Math.max(0, size - seeded.length);

  function addSeed(t: TournamentSeedTeam) {
    if (seeded.length >= size) return;
    setSeeded((s) => [...s, t.team_id]);
  }
  function removeSeed(teamId: string) {
    setSeeded((s) => s.filter((x) => x !== teamId));
  }

  async function publish() {
    setBusy(true);
    setErr(null);
    try {
      const body = random ? {} : { seeds: seeded.map((team_id, i) => ({ slot: i + 1, team_id })) };
      await api.publishTournament(eventId, body);
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to publish");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm("Delete this draft bracket?")) return;
    setBusy(true);
    try {
      await api.deleteTournament(eventId);
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to delete");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {size}-team bracket - {random ? "random seeding" : "manual seeding"}
          {byes > 0 && <span> - {byes} bye{byes === 1 ? "" : "s"} (top seeds)</span>}
        </p>
        <button type="button" onClick={remove} disabled={busy} className="text-xs text-destructive hover:underline">
          Delete draft
        </button>
      </div>

      {random ? (
        <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
          {pool.length} team{pool.length === 1 ? "" : "s"} on this event will be seeded randomly when you publish.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Seed order */}
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Seed order</h3>
            {seeded.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                Add teams from the right, in seed order (1 = top seed).
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5" data-region="seed-order">
                {seeded.map((teamId, i) => {
                  const t = byId.get(teamId);
                  return (
                    <li key={teamId} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E6F1FB] text-xs font-semibold text-[#0C447C]">{i + 1}</span>
                        <span className="font-medium">{t?.name ?? "?"}</span>
                        <span className="text-xs text-muted-foreground">{t?.school_name}</span>
                      </span>
                      <button type="button" onClick={() => removeSeed(teamId)} aria-label="Remove seed" className="rounded p-0.5 text-muted-foreground hover:text-destructive">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {/* Available teams */}
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Available teams</h3>
            {available.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">All teams seeded.</p>
            ) : (
              <ul className="flex flex-col gap-1.5" data-region="available-teams">
                {available.map((t) => (
                  <li key={t.team_id}>
                    <button
                      type="button"
                      onClick={() => addSeed(t)}
                      disabled={seeded.length >= size}
                      className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm transition hover:border-primary disabled:opacity-50"
                      data-action="add-seed"
                    >
                      <span>
                        <span className="font-medium">{t.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{t.school_name}</span>
                      </span>
                      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {err && <p className="text-sm text-destructive" data-error>{err}</p>}
      {!enough && (
        <p className="text-xs text-muted-foreground">
          Need at least {minTeams} teams for a {size}-team bracket (choose a smaller size for fewer teams).
        </p>
      )}
      <Button onClick={publish} disabled={busy || !enough} data-action="publish-bracket" className="w-fit">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trophy className="mr-2 h-4 w-4" />}
        Publish bracket
      </Button>
    </div>
  );
}

// --- Read-only bracket view (everyone) ------------------------------------

function BracketView({ rounds }: { rounds: TournamentRoundPublic[] }) {
  if (rounds.length === 0) return <NoBracket />;
  return (
    <div className="overflow-x-auto pb-2" data-region="bracket">
      <div className="flex min-w-fit gap-6">
        {rounds.map((round) => (
          <div key={round.id} className="flex min-w-[200px] flex-col gap-3" data-round={round.index}>
            <h3 className="text-sm font-semibold text-foreground">{round.name}</h3>
            <div className="flex flex-1 flex-col justify-around gap-3">
              {round.matches.map((m) => (
                <BracketMatch key={m.match_id} match={m} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BracketMatch({ match }: { match: TournamentMatchPublic }) {
  return (
    <Link
      to={`/matches/${match.match_id}`}
      className="block rounded-lg border border-border bg-background shadow-sm transition hover:border-primary"
      data-match={match.match_id}
    >
      <SlotRow slot={match.home} score={match.home_score} final={match.final} />
      <div className="border-t border-border/60" />
      <SlotRow slot={match.away} score={match.away_score} final={match.final} />
    </Link>
  );
}

function SlotRow({ slot, score, final }: { slot: TournamentSlot; score: number | null; final: boolean }) {
  const label = slot.is_bye ? "BYE" : slot.name ?? "TBD";
  const muted = slot.is_bye || !slot.name;
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2">
      <span className={`min-w-0 truncate text-sm ${slot.winner ? "font-semibold text-foreground" : muted ? "text-muted-foreground" : "text-foreground"}`}>
        {label}
        {slot.name && slot.school_name && (
          <span className="ml-1 text-xs text-muted-foreground">{slot.school_name}</span>
        )}
      </span>
      {final && score != null && (
        <span className={`shrink-0 text-sm tabular-nums ${slot.winner ? "font-semibold" : "text-muted-foreground"}`}>{score}</span>
      )}
    </div>
  );
}
